import { MessageSquareQuote, Sparkles } from "lucide-react";

export type QuestionBadgeSize = "sm" | "md" | "lg";
export type QuestionBadgeIcon = "quote" | "sparkles";

export interface QuestionBadgeProps {
  label?: string;
  size?: QuestionBadgeSize;
  icon?: QuestionBadgeIcon;
  className?: string;
}

const sizeClasses: Record<QuestionBadgeSize, string> = {
  sm: "text-[10px] px-1.5 py-0.5 gap-1",
  md: "text-[10px] sm:text-[11px] px-2 py-1 gap-1.5",
  lg: "text-[11px] sm:text-xs px-2.5 py-1 gap-1.5",
};

const iconSizes: Record<QuestionBadgeSize, string> = {
  sm: "w-2.5 h-2.5",
  md: "w-3 h-3",
  lg: "w-3.5 h-3.5",
};

/**
 * Canonical "Question" badge.
 *
 * Use this anywhere the app displays an answered question so users instantly
 * recognize the object as a question (not a podcast/episode). Pair with the
 * `.question-title` typography class for the question text itself.
 */
export default function QuestionBadge({
  label = "Question",
  size = "md",
  icon = "quote",
  className = "",
}: QuestionBadgeProps) {
  const Icon = icon === "sparkles" ? Sparkles : MessageSquareQuote;
  return (
    <span
      className={[
        "inline-flex items-center font-mono uppercase tracking-widest font-bold",
        "border-2 border-primary rounded",
        "bg-primary text-primary-foreground",
        sizeClasses[size],
        className,
      ].join(" ")}
    >
      <Icon className={iconSizes[size]} aria-hidden="true" />
      {label}
    </span>
  );
}
